import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../components/SEO';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck, FiArrowRight, FiExternalLink, FiPlus, FiCpu, FiLayout, FiSmartphone, FiZap } = FiIcons;

export default function PetContentPlanner() {
  const [activeFaq, setActiveFaq] = useState(null);

  const steps = [
    { title: "Open the Planner", desc: "Click the button to open the custom GPT tool." },
    { title: "Provide Details", desc: "Paste your website URL or answer 3 quick questions." },
    { title: "Get Your Plan", desc: "Receive hooks, captions, and visual ideas instantly." }
  ];

  const cards = [
    { 
      title: "5-Post Weekly Plan", 
      desc: "A Mon–Fri plan using the 2–2–1 mix (2 engagement, 2 educational, 1 promotional).",
      icon: FiLayout
    },
    { 
      title: "Captions + Visual Ideas", 
      desc: "Exactly what to post and what to say—no more staring at a blank screen.",
      icon: FiSmartphone
    },
    { 
      title: "Story Prompts", 
      desc: "Simple prompts and a repeatable routine to keep your stories active daily.",
      icon: FiZap
    }
  ];

  const faqs = [
    { 
      q: "Is this really free?", 
      a: "Yes. It's a free tool I've built to help pet business owners simplify their week and see the power of strategic content." 
    },
    { 
      q: "Do I need anything ready before I use it?", 
      a: "Just your website (optional) and 3 quick answers: your service area, what you do (dog walking, grooming, etc.), and your current goal." 
    }
  ];

  return (
    <>
      <SEO 
        title="Free Pet Content Planner | AI Social Media Tool | Otso Co" 
        description="Get a full week of social media content for your pet business in 60 seconds. Free AI tool for dog walkers, groomers, and pet sitters." 
        path="/pet-content-planner" 
      />

      {/* Hero Section */}
      <section className="bg-brand-white pt-20 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 bg-brand-blush/20 text-brand-primaryDark px-4 py-2 rounded-full text-sm font-bold mb-8"
          >
            <SafeIcon icon={FiCpu} /> Free AI Tool for Pet Pros
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-brand-primaryDark mb-6 leading-tight"
          >
            Get a Full Week of Social Media Content <br className="hidden md:block" /> 
            <span className="text-brand-mediumPurple font-black">in 60 Seconds</span>
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-gray-600 mb-10 leading-relaxed max-w-2xl mx-auto"
          >
            Paste your website + answer 3 quick questions. You’ll get a ready-to-post weekly plan built for local pet businesses—without overthinking.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-col items-center gap-4"
          >
            <a 
              href="https://chatgpt.com/g/g-69a66c0ee14081918a59af31ad9cef4c-pet-content-planner" 
              target="_blank" 
              rel="noopener noreferrer"
              className="w-full sm:w-auto bg-brand-primaryDark hover:bg-[#1f2552] text-white px-10 py-5 rounded-2xl font-bold text-xl transition-all shadow-xl flex items-center justify-center gap-3 group"
            >
              Open the Free Pet Content Planner 
              <SafeIcon icon={FiExternalLink} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </a>
            <div className="text-center">
              <p className="text-sm font-bold text-gray-400">Free. No call required.</p>
              <p className="text-[11px] text-gray-400 mt-1 uppercase tracking-widest">Opens in ChatGPT (free account required)</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-24 bg-brand-blush/10 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-brand-primaryDark mb-16 underline decoration-brand-blush decoration-4 underline-offset-8">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {steps.map((step, i) => (
              <div key={i} className="relative text-center">
                <div className="w-16 h-16 bg-brand-white border-2 border-brand-blush rounded-2xl flex items-center justify-center mx-auto mb-6 text-2xl font-black text-brand-primaryDark shadow-sm">
                  {i + 1}
                </div>
                <h3 className="text-xl font-bold text-brand-primaryDark mb-3">{step.title}</h3>
                <p className="text-gray-600 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-24 bg-brand-white px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {cards.map((card, i) => (
              <div key={i} className="bg-gray-50 p-8 rounded-3xl border border-gray-100 hover:border-brand-mediumPurple/30 transition-all flex flex-col h-full">
                <div className="text-brand-mediumPurple mb-6">
                  <SafeIcon icon={card.icon} size={40} />
                </div>
                <h3 className="text-xl font-bold text-brand-primaryDark mb-4">{card.title}</h3>
                <p className="text-gray-600 leading-relaxed flex-grow">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Preview Section */}
      <section className="py-24 bg-brand-primaryDark px-4 overflow-hidden">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <div className="w-full lg:w-1/2">
              <h2 className="text-3xl font-bold text-brand-blush mb-6">Preview: What Your Plan Looks Like</h2>
              <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                The planner doesn't just give you generic ideas. It builds a strategy around your business goals and service area.
              </p>
              <ul className="space-y-4">
                {['Pet sitters who are too busy to plan', 'Groomers who want consistent visibility', 'Trainers who want to build local trust'].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-white font-medium">
                    <SafeIcon icon={FiCheck} className="text-brand-blush flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="w-full lg:w-1/2">
              <div className="bg-white rounded-3xl shadow-2xl p-6 md:p-8 transform lg:rotate-2 border-l-8 border-brand-mediumPurple">
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-gray-100">
                  <div className="w-10 h-10 bg-brand-blush rounded-full"></div>
                  <div>
                    <p className="font-bold text-brand-primaryDark text-sm">Sample Monday Post</p>
                    <p className="text-xs text-gray-400 italic">Engagement Mix</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-xl">
                    <p className="text-xs font-black text-brand-mediumPurple uppercase mb-1">The Hook</p>
                    <p className="text-brand-primaryDark font-bold italic">"Does your dog do the 'happy dance' when they see the leash, or are they more of a slow-riser?"</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-xl">
                    <p className="text-xs font-black text-brand-mediumPurple uppercase mb-1">Visual Idea</p>
                    <p className="text-brand-primaryDark text-sm">A slow-motion video of a client's dog getting excited for a walk in [Your City].</p>
                  </div>
                  <div className="bg-brand-mediumPurple/10 p-4 rounded-xl">
                    <p className="text-xs font-black text-brand-mediumPurple uppercase mb-1">Caption Snippet</p>
                    <p className="text-brand-primaryDark text-sm leading-relaxed">We see all kinds of morning routines here at [Your Business], but the 'leash wiggle' is definitely our favorite...</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 bg-brand-white px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center text-brand-primaryDark mb-12">Common Questions</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="border border-gray-100 rounded-2xl overflow-hidden shadow-sm">
                <button 
                  onClick={() => setActiveFaq(activeFaq === i ? null : i)}
                  className="w-full flex justify-between items-center p-6 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-bold text-brand-primaryDark">{faq.q}</span>
                  <SafeIcon icon={FiPlus} className={`transition-transform duration-200 ${activeFaq === i ? 'rotate-45' : ''}`} />
                </button>
                <AnimatePresence>
                  {activeFaq === i && (
                    <motion.div 
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                    >
                      <div className="p-6 pt-0 text-gray-600 border-t border-gray-50 leading-relaxed">
                        {faq.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-4 bg-brand-mediumPurple text-brand-white text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-extrabold mb-6 leading-tight text-brand-primaryDark">Want me to post it for you?</h2>
          <p className="text-xl text-white/90 mb-10 leading-relaxed">
            If you'd rather hand this off, I offer done-for-you social media management and optional local ads when you're ready.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <Link 
              to="/services/social-media-management" 
              className="w-full sm:w-auto bg-brand-primaryDark hover:bg-[#1f2552] text-brand-white px-10 py-5 rounded-xl font-bold text-xl transition-all shadow-xl"
            >
              See Packages
            </Link>
            <a 
              href="https://tidycal.com/digital-by-perry/schedule-a-call" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-white font-bold hover:underline underline-offset-4"
            >
              Book a Call
            </a>
          </div>
        </div>
      </section>
    </>
  );
}